import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { NotificationBell } from './NotificationBell';

export function Layout({
  children,
  title,
  wide = false,
}: {
  children: ReactNode;
  title?: string;
  wide?: boolean;
}) {
  const links = [
    { to: '/dashboard', label: 'Dashboard', icon: '⌂' },
    { to: '/calendar', label: 'ปฏิทิน', icon: '◫' },
    { to: '/calendar/import', label: 'นำเข้าปฏิทิน', icon: '⇩' },
    { to: '/appointments', label: 'ใบนัด', icon: '▣' },
    { to: '/meetings', label: 'รายการประชุม', icon: '⌘' },
    { to: '/capture', label: 'สร้างนัดหมาย', icon: '⊕' },
    { to: '/settings', label: 'ตั้งค่า', icon: '⚙' },
  ];
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur lg:ml-64">
        <div className={`mx-auto flex h-16 items-center px-4 ${wide ? 'max-w-7xl' : 'max-w-4xl'}`}>
          <img src="/icon.svg" alt="" className="mr-3 h-9 w-9 rounded-xl" />
          <div>
            <p className="text-xs font-medium text-teal-700">นัดหมายจากภาพ</p>
            <h1 className="font-semibold text-slate-900">{title ?? 'จัดการใบนัด'}</h1>
          </div>
          <nav className="ml-auto hidden items-center gap-1 md:flex lg:hidden" aria-label="เมนูหลัก">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  clsx(
                    'rounded-xl px-3 py-2 text-sm font-medium transition',
                    isActive ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-100',
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <NotificationBell />
        </div>
      </header>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-slate-200 bg-white p-4 lg:block">
        <div className="flex items-center gap-3 border-b border-slate-100 pb-5"><img src="/icon.svg" alt="" className="h-10 w-10 rounded-xl" /><div><p className="text-xs font-medium text-teal-700">นัดหมายจากภาพ</p><p className="font-bold text-slate-900">ศูนย์บัญชาการ</p></div></div>
        <nav className="mt-5 space-y-1" aria-label="เมนูหลัก">
          {links.map((link) => <NavLink key={link.to} to={link.to} className={({ isActive }) => clsx('flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition', isActive ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-100')}><span className="text-lg" aria-hidden>{link.icon}</span>{link.label}</NavLink>)}
        </nav>
        <div className="absolute inset-x-4 bottom-5 rounded-xl bg-slate-50 p-3 text-xs text-slate-500">ระบบจัดการประชุมและเอกสาร</div>
      </aside>
      <main className={`mx-auto px-4 py-5 pb-28 lg:ml-64 ${wide ? 'max-w-7xl' : 'max-w-4xl'}`}>
        {children}
      </main>
      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white md:hidden">
        <div className="flex min-w-max gap-1 overflow-x-auto px-1">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                clsx(
                  'flex min-h-16 min-w-[72px] flex-col items-center justify-center gap-0.5 px-1 text-[11px] font-medium',
                  isActive ? 'text-teal-700' : 'text-slate-500',
                )
              }
            >
              <span className="text-xl" aria-hidden>
                {link.icon}
              </span>
              {link.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function LoadingScreen({ message = 'กำลังโหลด…' }: { message?: string }) {
  return (
    <div className="mx-auto max-w-lg animate-pulse space-y-4 p-5" aria-live="polite">
      <div className="h-8 w-2/3 rounded bg-slate-200" />
      <div className="h-32 rounded-2xl bg-slate-200" />
      <p className="text-center text-sm text-slate-500">{message}</p>
    </div>
  );
}

export function ErrorNotice({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800"
    >
      {message}
    </div>
  );
}

export function StatusPill({ status }: { status: string }) {
  const labels: Record<string, string> = {
    draft: 'ฉบับร่าง',
    image_uploaded: 'อัปโหลดแล้ว',
    ocr_queued: 'รออ่าน',
    ocr_processing: 'กำลังอ่าน',
    needs_review: 'รอตรวจสอบ',
    ready: 'พร้อมบันทึก',
    calendar_created: 'เพิ่มปฏิทินแล้ว',
    ocr_failed: 'อ่านไม่สำเร็จ',
  };
  const attention = ['needs_review', 'ocr_failed'].includes(status);
  return (
    <span
      className={clsx(
        'rounded-full px-2.5 py-1 text-xs font-semibold',
        attention ? 'bg-amber-100 text-amber-800' : 'bg-teal-50 text-teal-800',
      )}
    >
      {labels[status] ?? status}
    </span>
  );
}
